import React, { useState, useEffect } from 'react';
import {
  Box,
  IconButton,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverArrow,
  PopoverCloseButton,
  VStack,
  HStack,
  Text,
  Badge,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Divider,
  Button,
  Tooltip,
  useColorModeValue,
} from '@chakra-ui/react';
import { HiOutlineClipboardList } from 'react-icons/hi';

export default function QueueMonitor({ queueRef }) {
  const [stats, setStats] = useState({ pending: 0, running: 0, completed: 0, failed: 0, total: 0 });
  const [failedTasks, setFailedTasks] = useState([]);
  const bg = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');

  // Poll queue stats every second
  useEffect(() => {
    const updateStats = () => {
      if (queueRef.current) {
        const newStats = queueRef.current.getStats();
        setStats(newStats);
        setFailedTasks(queueRef.current.getFailedTasks());
      }
    };

    updateStats();
    const interval = setInterval(updateStats, 1000);

    return () => clearInterval(interval);
  }, [queueRef]);

  const handleClearHistory = () => {
    if (queueRef.current) {
      queueRef.current.clearHistory();
      setStats({ ...stats, completed: 0, failed: 0 });
      setFailedTasks([]);
    }
  };

  const getStatusColor = () => {
    if (stats.failed > 0) return 'red';
    if (stats.running > 0) return 'blue';
    if (stats.pending > 0) return 'orange';
    return 'gray';
  };

  const showBadge = stats.pending > 0 || stats.running > 0 || stats.failed > 0;

  return (
    <Popover placement="bottom-end">
      <PopoverTrigger>
        <Box position="relative" display="inline-block">
          <Tooltip label="Codify Queue Monitor" hasArrow>
            <IconButton
              icon={<HiOutlineClipboardList />}
              size="sm"
              variant="ghost"
              aria-label="Queue Monitor"
            />
          </Tooltip>
          {showBadge && (
            <Badge
              position="absolute"
              top="-1"
              right="-1"
              colorScheme={getStatusColor()}
              borderRadius="full"
              fontSize="0.7em"
              px={1.5}
              minW="18px"
              textAlign="center"
            >
              {stats.total}
            </Badge>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent minW="300px" bg={bg}>
        <PopoverArrow />
        <PopoverCloseButton />
        <PopoverHeader fontWeight="bold" fontSize="md">
          Codification Queue
        </PopoverHeader>
        <PopoverBody>
          <VStack align="stretch" spacing={3}>
            {/* Statistics Grid */}
            <HStack spacing={4} justify="space-around">
              <Stat size="sm">
                <StatLabel fontSize="xs">Pending</StatLabel>
                <StatNumber fontSize="2xl" color="orange.500">
                  {stats.pending}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Running</StatLabel>
                <StatNumber fontSize="2xl" color="blue.500">
                  {stats.running}
                </StatNumber>
              </Stat>
            </HStack>

            <Divider />

            <HStack spacing={4} justify="space-around">
              <Stat size="sm">
                <StatLabel fontSize="xs">Completed</StatLabel>
                <StatNumber fontSize="lg" color="green.500">
                  {stats.completed}
                </StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Failed</StatLabel>
                <StatNumber fontSize="lg" color="red.500">
                  {stats.failed}
                </StatNumber>
              </Stat>
            </HStack>

            {/* Failed Tasks List */}
            {failedTasks.length > 0 && (
              <>
                <Divider />
                <Box>
                  <Text fontSize="sm" fontWeight="bold" mb={2} color="red.600">
                    Recent Failures
                  </Text>
                  <VStack align="stretch" spacing={1} maxH="150px" overflowY="auto">
                    {failedTasks.slice(-5).reverse().map((task, idx) => (
                      <Box
                        key={idx}
                        p={2}
                        bg={hoverBg}
                        borderRadius="md"
                        fontSize="xs"
                      >
                        <HStack justify="space-between">
                          <Text isTruncated maxW="200px" title={task.key}>
                            Row: {task.key?.substring(0, 8)}...
                          </Text>
                          <Badge colorScheme="red" fontSize="0.6em">
                            {task.retries} retries
                          </Badge>
                        </HStack>
                        <Text color="gray.600" fontSize="0.7em" isTruncated title={task.error}>
                          {task.error}
                        </Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </>
            )}

            {/* Actions */}
            {(stats.completed > 0 || stats.failed > 0) && (
              <>
                <Divider />
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleClearHistory}
                  width="full"
                >
                  Clear History
                </Button>
              </>
            )}

            {/* Empty State */}
            {stats.total === 0 && stats.completed === 0 && stats.failed === 0 && (
              <Text fontSize="sm" color="gray.500" textAlign="center" py={2}>
                Queue is empty
              </Text>
            )}
          </VStack>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  );
}
